import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, SearchIcon, FilterIcon, ClockIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, UploadIcon, CalendarIcon, CreditCardIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, XIcon, ArrowUpRightIcon, CheckSquareIcon, UndoIcon, FileClockIcon, LoaderIcon, TrophyIcon, CheckIcon, MoreVerticalIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon, InfoIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeReason, DisputeStage } from '../../../shared/types/Dispute';
import { generateDisputes } from '../utils/disputes';
type DateFilterType = 'response_due' | 'disputed_time' | 'last_updated';
type SortField = 'dueDate' | 'amount' | 'reason' | 'stage' | 'status' | 'paymentMethod' | 'customerName';
type SortDirection = 'asc' | 'desc' | null;
type QuickFilterType = 'requires_response' | 'challenged' | 'all' | 'won_reversed' | 'accepted_lost' | null;
export function Disputes() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<DisputeReason | 'all'>('all');
  const [stageFilter, setStageFilter] = useState<DisputeStage | 'all'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('response_due');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilterType>('requires_response');
  const [selectedDisputes, setSelectedDisputes] = useState<Set<string>>(new Set());
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [hoveredReason, setHoveredReason] = useState<string | null>(null);
  // Use centralized mock data
  const disputes = useMemo(() => generateDisputes(), []);
  // Calculate statistics
  const totalDisputes = disputes.length;
  const requiresResponseCount = disputes.filter(d => d.status === 'requires_response').length;
  const challengedCount = disputes.filter(d => d.status === 'challenged').length;
  const wonOrReversedCount = disputes.filter(d => d.status === 'won' || d.status === 'reversed').length;
  const acceptedOrLostCount = disputes.filter(d => d.status === 'accepted' || d.status === 'lost').length;
  // Get the last updated date for a dispute
  const getLastUpdated = (dispute: Dispute): string => {
    if (dispute.lifecycle && dispute.lifecycle.length > 0) {
      const lastEvent = dispute.lifecycle[dispute.lifecycle.length - 1];
      return lastEvent.timestamp;
    }
    return dispute.createdAt;
  };
  // Handle quick filter clicks
  const handleQuickFilter = (filterType: QuickFilterType) => {
    if (activeQuickFilter === filterType) {
      // Clicking the same filter again deactivates it
      setActiveQuickFilter(null);
      setStatusFilter('all');
    } else {
      setActiveQuickFilter(filterType);
      // Update the status filter based on quick filter
      switch (filterType) {
        case 'requires_response':
          setStatusFilter('requires_response');
          break;
        case 'challenged':
          setStatusFilter('challenged');
          break;
        case 'all':
          setStatusFilter('all');
          break;
        case 'won_reversed':
          // This will be handled in the filter logic
          setStatusFilter('all');
          break;
        case 'accepted_lost':
          // This will be handled in the filter logic
          setStatusFilter('all');
          break;
      }
    }
  };
  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Filter and sort disputes
  const filteredDisputes = useMemo(() => {
    let filtered = disputes.filter(dispute => {
      const matchesSearch = dispute.disputeId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
      // Handle quick filter logic
      let matchesQuickFilter = true;
      if (activeQuickFilter === 'won_reversed') {
        matchesQuickFilter = dispute.status === 'won' || dispute.status === 'reversed';
      } else if (activeQuickFilter === 'accepted_lost') {
        matchesQuickFilter = dispute.status === 'accepted' || dispute.status === 'lost';
      } else if (activeQuickFilter && activeQuickFilter !== 'all') {
        matchesQuickFilter = dispute.status === activeQuickFilter;
      }
      const matchesStatus = statusFilter === 'all' || dispute.status === statusFilter;
      const matchesReason = reasonFilter === 'all' || dispute.reason === reasonFilter;
      const matchesStage = stageFilter === 'all' || dispute.stage === stageFilter;
      const matchesPaymentMethod = paymentMethodFilter === 'all' || dispute.paymentMethod === paymentMethodFilter;
      // Date filtering
      let matchesDateRange = true;
      if (dateFrom || dateTo) {
        let dateToCheck: Date;
        if (dateFilterType === 'response_due') {
          dateToCheck = new Date(dispute.dueDate);
        } else if (dateFilterType === 'disputed_time') {
          dateToCheck = new Date(dispute.createdAt);
        } else {
          dateToCheck = new Date(getLastUpdated(dispute));
        }
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (dateToCheck < fromDate) matchesDateRange = false;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (dateToCheck > toDate) matchesDateRange = false;
        }
      }
      return matchesSearch && matchesQuickFilter && matchesStatus && matchesReason && matchesStage && matchesPaymentMethod && matchesDateRange;
    });
    // Apply sorting
    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortField) {
          case 'dueDate':
            aValue = new Date(a.dueDate).getTime();
            bValue = new Date(b.dueDate).getTime();
            break;
          case 'amount':
            aValue = a.amount;
            bValue = b.amount;
            break;
          case 'reason':
            aValue = getReasonLabel(a.reason);
            bValue = getReasonLabel(b.reason);
            break;
          case 'stage':
            aValue = getStageLabel(a.stage);
            bValue = getStageLabel(b.stage);
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'paymentMethod':
            aValue = getPaymentMethodLabel(a.paymentMethod);
            bValue = getPaymentMethodLabel(b.paymentMethod);
            break;
          case 'customerName':
            aValue = a.customerName;
            bValue = b.customerName;
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
      });
    } else {
      // Default sorting: Requires response first, then by closest deadline
      filtered.sort((a, b) => {
        const aRequiresResponse = a.status === 'requires_response';
        const bRequiresResponse = b.status === 'requires_response';
        if (aRequiresResponse && !bRequiresResponse) return -1;
        if (!aRequiresResponse && bRequiresResponse) return 1;
        const aDate = new Date(a.dueDate).getTime();
        const bDate = new Date(b.dueDate).getTime();
        return aDate - bDate;
      });
    }
    return filtered;
  }, [disputes, searchQuery, statusFilter, reasonFilter, stageFilter, paymentMethodFilter, dateFilterType, dateFrom, dateTo, sortField, sortDirection, activeQuickFilter]);
  const getStatusIcon = (status: DisputeStatus) => {
    const iconSize = 14;
    const iconClass = 'mr-1.5';
    switch (status) {
      case 'requires_response':
        return <AlertCircleIcon size={iconSize} className={iconClass} data-id="element-7147" data-mp="i2ti2idf2ol3" />;
      case 'expired':
        return <XIcon size={iconSize} className={iconClass} data-id="element-7148" data-mp="d3gl34su014s" />;
      case 'challenged':
        return <ArrowUpRightIcon size={iconSize} className={iconClass} data-id="element-7149" data-mp="855c0wad1unm" />;
      case 'accepted':
        return <CheckSquareIcon size={iconSize} className={iconClass} data-id="element-7150" data-mp="454yddv35nhm" />;
      case 'reversed':
        return <UndoIcon size={iconSize} className={iconClass} data-id="element-7151" data-mp="3vcqyiwilj7tq" />;
      case 'pending_closure':
        return <FileClockIcon size={iconSize} className={iconClass} data-id="element-7152" data-mp="1yie02jhe44pb" />;
      case 'pending_decision':
        return <LoaderIcon size={iconSize} className={iconClass} data-id="element-7153" data-mp="1tkcffu4bfxbq" />;
      case 'won':
        return <TrophyIcon size={iconSize} className={iconClass} data-id="element-7154" data-mp="262554oat0aoc" />;
      case 'lost':
        return <XCircleIcon size={iconSize} className={iconClass} data-id="element-7155" data-mp="2156g2k5zytx0" />;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: DisputeStatus) => {
    // Using exact colors from the screenshot
    const styles = {
      requires_response: 'text-[#92400E] bg-[#FFEDD5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#7C2D12] bg-[#FFEDD5]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-7156" data-mp="3f4844tc11e1i">
        {getStatusIcon(status)}
        {labels[status]}
      </span>;
  };
  const getStageLabel = (stage: DisputeStage) => {
    const labels = {
      rfi: 'RFI',
      pre_chargeback: 'Pre-chargeback',
      chargeback: 'Chargeback',
      pre_arbitration: 'Pre-arbitration',
      arbitration: 'Arbitration'
    };
    return labels[stage];
  };
  const getReasonLabel = (reason: DisputeReason) => {
    const labels = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      card: 'Card',
      bank_transfer: 'Bank Transfer',
      wallet: 'Wallet'
    };
    return labels[method] || method;
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', {
      month: 'short'
    });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const getDaysRemaining = (dueDate: string) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const getDaysLeftBadge = (dueDate: string) => {
    const hours = getHoursRemaining(dueDate);
    const days = getDaysRemaining(dueDate);
    // If overdue
    if (hours < 0) {
      const overdueHours = Math.abs(hours);
      if (overdueHours < 24) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7157" data-mp="2tt2unri44spz">
            {t('pages.getDaysLeftBadge.overdue_by')} {overdueHours}{' '}
            {overdueHours === 1 ? 'hour' : 'hours'}
          </span>;
      } else {
        const overdueDays = Math.abs(days);
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7158" data-mp="7mg34xg5ixbk">
            {t('pages.getDaysLeftBadge.overdue_by')} {overdueDays}{' '}
            {overdueDays === 1 ? 'day' : 'days'}
          </span>;
      }
    }
    // If due within 24 hours, show hours
    if (hours <= 24) {
      if (hours === 0) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7159" data-mp="qjhauq13bbj7">
            {t('pages.getDaysLeftBadge.due_in_less_than_1_hour')}
          </span>;
      } else if (hours === 1) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7160" data-mp="vb9458bmrvd1">
            {t('pages.ChallengeDispute.due_in_1_hour')}
          </span>;
      } else {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7161" data-mp="15a83u1ef4gnw">
            {t('pages.DisputeDetail.due_in')} {hours}{' '}
            {t('pages.getDaysLeftBadge.hours')}
          </span>;
      }
    }
    // Otherwise show days
    if (days <= 2) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-7162" data-mp="1ewj2b55hkk5r">
          {days} {days === 1 ? 'day' : 'days'}{' '}
          {t('pages.getDaysLeftBadge.left')}
        </span>;
    } else if (days <= 7) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-7163" data-mp="19yzog6cpa9ec">
          {days} {t('pages.getDaysLeftBadge.days_left')}
        </span>;
    } else {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-200 text-[#8C6C00]" data-id="element-7164" data-mp="108eifxup62le">
          {days} {t('pages.getDaysLeftBadge.days_left')}
        </span>;
    }
  };
  const handleAcceptDispute = (disputeId: string) => {
    setOpenDropdownId(null);
    console.log('Accepting dispute:', disputeId);
    // In a real app, this would call an API
  };
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDownIcon size={14} className="ml-1 text-gray-400" data-id="element-7165" data-mp="2k5dj1hmvgmf5" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUpIcon size={14} className="ml-1 text-blue-600" data-id="element-7166" data-mp="2f6siti7me2qp" />;
    }
    return <ArrowDownIcon size={14} className="ml-1 text-blue-600" data-id="element-7167" data-mp="3bvrtt4zl9iml" />;
  };
  const handleRowClick = (disputeId: string, e: React.MouseEvent) => {
    // Don't navigate if clicking on the actions dropdown
    const target = e.target as HTMLElement;
    if (target.closest('.actions-dropdown')) {
      return;
    }
    navigate(`/dispute-detail/${disputeId}`);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableDisputes = filteredDisputes.filter(d => d.status === 'requires_response').map(d => d.id);
      setSelectedDisputes(new Set(selectableDisputes));
    } else {
      setSelectedDisputes(new Set());
    }
  };
  const handleSelectDispute = (disputeId: string, checked: boolean) => {
    const newSelected = new Set(selectedDisputes);
    if (checked) {
      newSelected.add(disputeId);
    } else {
      newSelected.delete(disputeId);
    }
    setSelectedDisputes(newSelected);
  };
  const handleBulkAccept = () => {
    setShowBulkAcceptModal(true);
  };
  const confirmBulkAccept = () => {
    console.log('Accepting disputes:', Array.from(selectedDisputes));
    // In a real app, this would call an API
    setSelectedDisputes(new Set());
    setShowBulkAcceptModal(false);
  };
  const getReasonTooltip = (reason: DisputeReason): string => {
    const tooltips: Record<DisputeReason, string> = {
      credit_not_processed: 'The customer says they were owed a refund or credit, but it never showed up on their card statement.',
      duplicate: 'The customer believes they were charged more than once, or also paid another way, for the same purchase.',
      fraudulent: "The customer claims they didn't authorize or recognize this transaction on their card.",
      general: 'The bank believes the transaction was processed incorrectly (for example, wrong type or wrong amount).',
      product_not_received: 'The customer says they never received the goods or services by the expected date.',
      product_unacceptable: 'The customer says the goods or services were defective, damaged, or not as described.',
      subscription_canceled: 'The customer says they were charged again even though they believe the subscription was already canceled.',
      unrecognized: "The customer doesn't recognize this transaction.",
      incorrect_account_details: 'Incorrect account details were used for the transaction.',
      insufficient_funds: 'The account had insufficient funds for the transaction.',
      bank_cannot_process: 'The bank cannot process this transaction.',
      debit_not_authorized: 'The debit was not authorized by the customer.',
      customer_initiated: 'The dispute was initiated by the customer.',
      check_returned: 'The check was returned.',
      noncompliant: 'The transaction was noncompliant with regulations.'
    };
    return tooltips[reason] || '';
  };
  const selectableDisputesCount = filteredDisputes.filter(d => d.status === 'requires_response').length;
  const allSelectableSelected = selectableDisputesCount > 0 && selectedDisputes.size === selectableDisputesCount;
  const someSelected = selectedDisputes.size > 0 && !allSelectableSelected;
  return <div className="space-y-6" data-id="element-7168" data-mp="3vj7x4gopz605">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-7169" data-mp="4cetz7ad8wsz">
        <button onClick={() => handleQuickFilter('requires_response')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'requires_response' ? 'border-[#E2231A] ring-2 ring-[#FBD4D1]' : 'border-gray-200 hover:border-[#EB4A42]'}`} data-id="element-7170" data-mp="4au3ptv3400x">
          <div className="flex items-center justify-between" data-id="element-7171" data-mp="2tdf1nnirjh9n">
            <div data-id="element-7172" data-mp="2tcvn8gsbnb0d">
              <div className="text-sm font-medium text-gray-500" data-id="element-7173" data-mp="2tfhu0e7e1mhj">
                {t('pages.Disputes.requires_response')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-7174" data-mp="2jmtvnf0rfagp">
                {requiresResponseCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'requires_response' ? 'bg-[#F1786F]' : 'bg-[#FBD4D1]'}`} data-id="element-7175" data-mp="25qgnwxm12nff">
              <AlertCircleIcon size={20} className="text-[#9F140F]" aria-hidden="true" data-id="element-7176" data-mp="1vs9aupuv0pip" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('challenged')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'challenged' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-7177" data-mp="3q24kbg27sdwo">
          <div className="flex items-center justify-between" data-id="element-7178" data-mp="1eeraidcz15xl">
            <div data-id="element-7179" data-mp="1t69xiv4900qg">
              <div className="text-sm font-medium text-gray-500" data-id="element-7180" data-mp="1t6t79ojqvite">
                {t('pages.Disputes.challenged')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-7181" data-mp="225wpkqb37u6u">
                {challengedCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'challenged' ? 'bg-blue-200' : 'bg-blue-100'}`} data-id="element-7182" data-mp="1x6ad4j332hz3">
              <ArrowUpRightIcon size={20} className="text-blue-600" data-id="element-7183" data-mp="276az6csc7c6z" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('all')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'all' ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-7184" data-mp="hqv23vrotgxg">
          <div className="flex items-center justify-between" data-id="element-7185" data-mp="16i0sqargmjuv">
            <div data-id="element-7186" data-mp="1bgc08eua8i9x">
              <div className="text-sm font-medium text-gray-500" data-id="element-7187" data-mp="1bfss1pufp893">
                {t('pages.Disputes.all_disputes')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-7188" data-mp="1ggghw86xfj1q">
                {totalDisputes}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'all' ? 'bg-gray-200' : 'bg-gray-100'}`} data-id="element-7189" data-mp="1pflh7gqbbgey">
              <AlertTriangleIcon size={20} className="text-gray-600" data-id="element-7190" data-mp="1u8weor98iku7" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('won_reversed')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'won_reversed' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-green-300'}`} data-id="element-7191" data-mp="244zrcr1pq1ve">
          <div className="flex items-center justify-between" data-id="element-7192" data-mp="3om8x48ipytc7">
            <div data-id="element-7193" data-mp="3oov5gadfpcvh">
              <div className="text-sm font-medium text-gray-500" data-id="element-7194" data-mp="3jnpae5waxdis">
                {t('pages.Disputes.won_or_reversed')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-7195" data-mp="3epnq5hn5385q">
                {wonOrReversedCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'won_reversed' ? 'bg-green-200' : 'bg-green-100'}`} data-id="element-7196" data-mp="g8t7rrfy69xf">
              <TrophyIcon size={20} className="text-green-600" data-id="element-7197" data-mp="6f2mswj14hmh" />
            </div>
          </div>
        </button>

        <button onClick={() => handleQuickFilter('accepted_lost')} className={`bg-white p-4 rounded-lg border-2 shadow-sm text-left transition-all ${activeQuickFilter === 'accepted_lost' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200 hover:border-red-300'}`} data-id="element-7198" data-mp="yxgc27qauv9y">
          <div className="flex items-center justify-between" data-id="element-7199" data-mp="jntf0isg68mw">
            <div data-id="element-7200" data-mp="jpe9y9iobtla">
              <div className="text-sm font-medium text-gray-500" data-id="element-7201" data-mp="jlqgfad8b76k">
                {t('pages.Disputes.accepted_or_lost')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-7202" data-mp="9p3z2vk42y9a">
                {acceptedOrLostCount}
              </div>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeQuickFilter === 'accepted_lost' ? 'bg-red-200' : 'bg-red-100'}`} data-id="element-7203" data-mp="xfzsty84o3j6">
              <XCircleIcon size={20} className="text-red-600" data-id="element-7204" data-mp="novft420isqz" />
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-7205" data-mp="1gec53suc5tiz">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-7206" data-mp="1gc96wlj14u36">
          <div className="flex flex-col gap-4" data-id="element-7207" data-mp="1l6de95w7j44s">
            {/* Search bar */}
            <div className="relative flex-grow" data-id="element-7208" data-mp="35u62wmgfxyd5">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7209" data-mp="35suyckf68mkz">
                <SearchIcon size={16} className="text-gray-400" data-id="element-7210" data-mp="30ua7hblt7f97" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.Disputes.attrs.search_by_dispute_id_transaction_id')} data-id="element-7211" data-mp="3fn3oj1gqc3fz" />
            </div>

            {/* Dropdown filters */}
            <div className="flex gap-2 flex-wrap" data-id="element-7212" data-mp="2rtvcfkj1mlsi">
              <select value={stageFilter} onChange={e => setStageFilter(e.target.value as DisputeStage | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-7213" data-mp="b6j4w0cqj5yp">
                <option value="all" data-id="element-7214" data-mp="34wxw0hx7xjd5">{t('pages.Disputes.all_stages')}</option>
                <option value="rfi" data-id="element-7215" data-mp="39z6qxjtnivhw">{t('pages.Disputes.rfi')}</option>
                <option value="pre_chargeback" data-id="element-7216" data-mp="3dzz24im33agt">
                  {t('pages.Disputes.prechargeback')}
                </option>
                <option value="chargeback" data-id="element-7217" data-mp="2hahmpbxaua1x">
                  {t('pages.Disputes.chargeback')}
                </option>
                <option value="pre_arbitration" data-id="element-7218" data-mp="2m8j5dvqtc7cv">
                  {t('pages.Disputes.prearbitration')}
                </option>
                <option value="arbitration" data-id="element-7219" data-mp="2v0l2bk1ob5ie">
                  {t('pages.Disputes.arbitration')}
                </option>
              </select>

              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as DisputeStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-7220" data-mp="1yhnk7hgv4ael">
                <option value="all" data-id="element-7221" data-mp="1m02wb0749d0z">{t('pages.Disputes.all_statuses')}</option>
                <option value="requires_response" data-id="element-7222" data-mp="1m27acayqbsez">
                  {t('pages.Disputes.requires_response')}
                </option>
                <option value="expired" data-id="element-7223" data-mp="1c1n938b7mnwr">{t('pages.Disputes.expired')}</option>
                <option value="challenged" data-id="element-7224" data-mp="1c3rgi08bi51s">
                  {t('pages.Disputes.challenged')}
                </option>
                <option value="accepted" data-id="element-7225" data-mp="12bu44q5kp71h">{t('pages.Disputes.accepted')}</option>
                <option value="reversed" data-id="element-7226" data-mp="yaidqievkhs8">{t('pages.Disputes.reversed')}</option>
                <option value="pending_closure" data-id="element-7227" data-mp="y8fdz6nx7aab">
                  {t('pages.Disputes.pending_closure')}
                </option>
                <option value="pending_decision" data-id="element-7228" data-mp="ocm5bydbo0xt">
                  {t('pages.Disputes.pending_decision')}
                </option>
                <option value="won" data-id="element-7229" data-mp="2ca3hgtqdby19">{t('pages.Disputes.won')}</option>
                <option value="lost" data-id="element-7230" data-mp="2h7v5hcnw7006">{t('pages.Disputes.lost')}</option>
              </select>

              <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as DisputeReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-7231" data-mp="238m1rcgvhyd3">
                <option value="all" data-id="element-7232" data-mp="1yau21w9zl7yc">{t('pages.Disputes.all_reasons')}</option>
                <option value="duplicate" data-id="element-7233" data-mp="1kivzy5ep0cbo">
                  {t('pages.Disputes.duplicate')}
                </option>
                <option value="fraudulent" data-id="element-7234" data-mp="1ojz9ka65dc4g">
                  {t('pages.Disputes.fraudulent')}
                </option>
                <option value="subscription_canceled" data-id="element-7235" data-mp="1alrhafk22yzm">
                  {t('pages.Disputes.subscription_canceled')}
                </option>
                <option value="product_unacceptable" data-id="element-7236" data-mp="vw6cg4bmmuur">
                  {t('pages.Disputes.product_unacceptable')}
                </option>
                <option value="product_not_received" data-id="element-7237" data-mp="qzpphdg5ymj9">
                  {t('pages.Disputes.product_not_received')}
                </option>
                <option value="unrecognized" data-id="element-7238" data-mp="10wc8rxsud5j6">
                  {t('pages.Disputes.unrecognized')}
                </option>
                <option value="credit_not_processed" data-id="element-7239" data-mp="1ev28ll71pjb8">
                  {t('pages.Disputes.credit_not_processed')}
                </option>
                <option value="incorrect_account_details" data-id="element-7240" data-mp="19wh6thc5arko">
                  {t('pages.Disputes.incorrect_account_details')}
                </option>
                <option value="insufficient_funds" data-id="element-7241" data-mp="1jmafa9gzqqkp">
                  {t('pages.Disputes.insufficient_funds')}
                </option>
                <option value="bank_cannot_process" data-id="element-7242" data-mp="1xmuw503cicbk">
                  {t('pages.Disputes.bank_cannot_process')}
                </option>
                <option value="debit_not_authorized" data-id="element-7243" data-mp="2uzetlgomo303">
                  {t('pages.Disputes.debit_not_authorized')}
                </option>
                <option value="general" data-id="element-7244" data-mp="2q1det7y8ipuf">{t('pages.Disputes.general')}</option>
                <option value="customer_initiated" data-id="element-7245" data-mp="2l0pm8hucy0y3">
                  {t('pages.Disputes.customer_initiated')}
                </option>
                <option value="check_returned" data-id="element-7246" data-mp="2gz43ifv56sfz">
                  {t('pages.Disputes.check_returned')}
                </option>
                <option value="noncompliant" data-id="element-7247" data-mp="2c2dprz6t88xq">
                  {t('pages.Disputes.noncompliant')}
                </option>
              </select>

              <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-7248" data-mp="22bstiielzghz">
                <option value="all" data-id="element-7249" data-mp="1ocaz9nijnb18">
                  {t('pages.Disputes.all_payment_methods')}
                </option>
                <option value="card" data-id="element-7250" data-mp="3so1k625lnh3">{t('pages.Disputes.card')}</option>
                <option value="bank_transfer" data-id="element-7251" data-mp="8qzbshwd1zzu">
                  {t('pages.Disputes.bank_transfer')}
                </option>
                <option value="wallet" data-id="element-7252" data-mp="cuf2yypnv855">{t('pages.Disputes.wallet')}</option>
              </select>
            </div>

            {/* Date filters */}
            <div className="flex gap-2 flex-wrap items-end" data-id="element-7253" data-mp="3h8dk4elmggb2">
              <div className="flex-shrink-0" data-id="element-7254" data-mp="3h5r55tweiyit">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-7255" data-mp="3l8nsfxwvzit3">
                  {t('pages.Disputes.filter_by')}
                </label>
                <select value={dateFilterType} onChange={e => setDateFilterType(e.target.value as DateFilterType)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-7256" data-mp="3q6p8e9z0n20c">
                  <option value="response_due" data-id="element-7257" data-mp="9n62k3jpn2b9">
                    {t('pages.ChallengeDispute.response_due')}
                  </option>
                  <option value="disputed_time" data-id="element-7258" data-mp="4njoxszyzk1x">
                    {t('pages.Disputes.disputed_time')}
                  </option>
                  <option value="last_updated" data-id="element-7259" data-mp="eexlbrwmk3qu">
                    {t('pages.Disputes.last_updated')}
                  </option>
                </select>
              </div>

              <div className="flex-shrink-0" data-id="element-7260" data-mp="3hvwc0kukmqea">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-7261" data-mp="3hx7nz7y674gh">
                  {t('pages.Disputes.from')}
                </label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="block px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-7262" data-mp="3qwkqfgnvmsrp" />
              </div>

              <div className="flex-shrink-0" data-id="element-7263" data-mp="1enm9mlzx2fh3">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-7264" data-mp="1elt08yxx5h5e">
                  {t('pages.Disputes.to')}
                </label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="block px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-7265" data-mp="14pg6y26ad5b6" />
              </div>

              {(dateFrom || dateTo) && <button onClick={() => {
              setDateFrom('');
              setDateTo('');
            }} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-7266" data-mp="22cf8aq7xb1fe">
                  {t('pages.Disputes.clear_dates')}
                </button>}
            </div>
          </div>
        </div>

        {/* Disputes List */}
        <div className="overflow-x-auto" data-id="element-7267" data-mp="whvhz7o5mjyj">
          {selectedDisputes.size > 0 && <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between" data-id="element-7268" data-mp="sfidsnp22u3v">
              <div className="flex items-center gap-3" data-id="element-7269" data-mp="ng5jjhvuukr9">
                <span className="text-sm font-medium text-blue-900" data-id="element-7270" data-mp="niryw3ozm4k1">
                  {selectedDisputes.size} {t('pages.Disputes.dispute')}
                  {selectedDisputes.size !== 1 ? 's' : ''}{' '}
                  {t('pages.Disputes.selected')}
                </span>
                <button onClick={() => setSelectedDisputes(new Set())} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-7271" data-mp="1b7b76j81ehc4">
                  {t('pages.Disputes.clear_selection')}
                </button>
              </div>
              <button onClick={handleBulkAccept} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-7272" data-mp="1ywvtnc3efwq5">
                <CheckIcon size={16} className="mr-2" data-id="element-7273" data-mp="21icynbu2qgu4" />
                {t('pages.Disputes.accept_selected')}
              </button>
            </div>}
          <table className="min-w-full divide-y divide-gray-200" data-id="element-7274" data-mp="2begbbbmjxxvb">
            <thead className="bg-gray-50" data-id="element-7275" data-mp="2gdsxrv9ih2xd">
              <tr data-id="element-7276" data-mp="2geu7nwesjjdi">
                <th className="px-6 py-3 text-left" data-id="element-7277" data-mp="2gfdrxjrwyjup">
                  <input type="checkbox" checked={allSelectableSelected} ref={el => {
                  if (el) {
                    el.indeterminate = someSelected;
                  }
                }} onChange={e => handleSelectAll(e.target.checked)} disabled={selectableDisputesCount === 0} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-7278" data-mp="2kfxpgogefphd" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('dueDate')} data-id="element-7279" data-mp="3858gvfbv07iu">
                  <div className="flex items-center" data-id="element-7280" data-mp="1hsaef9w5x1lo">
                    {t('pages.Disputes.response_due')}
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('amount')} data-id="element-7281" data-mp="1rqgd7imo9rmb">
                  <div className="flex items-center" data-id="element-7282" data-mp="20q3gk9yyp7i0">
                    {t('pages.Disputes.disputed_amount')}
                    {getSortIcon('amount')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('reason')} data-id="element-7283" data-mp="6e56y6wqq9ll">
                  <div className="flex items-center" data-id="element-7284" data-mp="3ofc42jr9579e">
                    {t('pages.DisputeDetail.reason')}
                    {getSortIcon('reason')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('stage')} data-id="element-7285" data-mp="3elbt419ah7sr">
                  <div className="flex items-center" data-id="element-7286" data-mp="39ouu2c0jx90w">
                    {t('pages.Disputes.stage')}
                    {getSortIcon('stage')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')} data-id="element-7287" data-mp="30ndhv4q9lrhh">
                  <div className="flex items-center" data-id="element-7288" data-mp="yv8qpvglay7t">
                    {t('pages.DisputeDetail.status')}
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('paymentMethod')} data-id="element-7289" data-mp="22inyp4gc3bz9">
                  <div className="flex items-center" data-id="element-7290" data-mp="1tn858ojhqyu7">
                    {t('pages.Disputes.payment_method')}
                    {getSortIcon('paymentMethod')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customerName')} data-id="element-7291" data-mp="1jthlty281eld">
                  <div className="flex items-center" data-id="element-7292" data-mp="396cna7t61q5l">
                    {t('pages.Disputes.customer')}
                    {getSortIcon('customerName')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7293" data-mp="qxi2kwqt2hf0">
                  {t('pages.Disputes.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-7294" data-mp="10ntcr1131muc">
              {filteredDisputes.map(dispute => {
              const isSelectable = dispute.status === 'requires_response';
              const isSelected = selectedDisputes.has(dispute.id);
              return <tr key={dispute.id} className="hover:bg-gray-50 cursor-pointer" onClick={e => handleRowClick(dispute.id, e)} data-id="element-7295" data-mp="cxql2m12yiw9">
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()} data-id="element-7296" data-mp="lz6wmx3msr22">
                      <input type="checkbox" checked={isSelected} onChange={e => handleSelectDispute(dispute.id, e.target.checked)} disabled={!isSelectable} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-30" data-id="element-7297" data-mp="1xcitgo0z49eq" />
                    </td>
                    <td className="px-6 py-4" data-id="element-7298" data-mp="28muii1gy16nn">
                      <div className="text-sm text-gray-900" data-id="element-7299" data-mp="2dn8s3ny70ons">
                        {formatDate(dispute.dueDate)}
                      </div>
                      <div className="mt-1" data-id="element-7300" data-mp="2ila8g148i9vk">
                        {getDaysLeftBadge(dispute.dueDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-7301" data-mp="2renabska0lts">
                      <div className="text-sm font-medium text-gray-900" data-id="element-7302" data-mp="2rhjalnjljmle">
                        {dispute.currency}{' '}
                        {(dispute.amount / 100).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-7303" data-mp="36hov2ynydasd">
                        {dispute.disputeId}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-7304" data-mp="2xi1rq7bnxuwo">
                      <div className="flex items-center gap-1" data-id="element-7305" data-mp="2xjd1qovpbyw8">
                        <span className="text-sm text-gray-900" data-id="element-7306" data-mp="3u8u712qrvixe">
                          {getReasonLabel(dispute.reason)}
                        </span>
                        <div className="relative inline-block" data-id="element-7307" data-mp="3p9zmnwd5pmb6">
                          <div onMouseEnter={() => setHoveredReason(dispute.id)} onMouseLeave={() => setHoveredReason(null)} className="cursor-help" data-id="element-7308" data-mp="3khg1oec53owc">
                            <InfoIcon size={14} className="text-gray-400" data-id="element-7309" data-mp="c2pioq9mze46" />
                          </div>
                          {hoveredReason === dispute.id && <div className="absolute z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2" data-id="element-7310" data-mp="26bbh31k9mzs2">
                              {getReasonTooltip(dispute.reason)}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1" data-id="element-7311" data-mp="21jbe108wfqr9">
                                <div className="border-4 border-transparent border-t-gray-900" data-id="element-7312" data-mp="1njsir816s9wd"></div>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-7313" data-mp="1ikz5ngptakwq">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-7314" data-mp="1ilqors282q64">
                        {getStageLabel(dispute.stage)}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-7315" data-mp="11bmsqtck5pv3">
                      {getStatusBadge(dispute.status)}
                    </td>
                    <td className="px-6 py-4" data-id="element-7316" data-mp="wdbi4gefvbbt">
                      <div className="text-sm text-gray-900" data-id="element-7317" data-mp="wagxanrijohv">
                        {getPaymentMethodLabel(dispute.paymentMethod)}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-7318" data-mp="169olk5zsd3jr">
                        {dispute.cardBrand} •••• {dispute.cardLast4}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-7319" data-mp="1g12gs1kpfh73">
                      <div data-id="element-7320" data-mp="1g09jrsbjs625">
                        <div className="text-sm font-medium text-gray-900" data-id="element-7321" data-mp="1u0afktcygnar">
                          {dispute.customerName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-7322" data-mp="1p1qv6xe0fjki">
                          {dispute.customerEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()} data-id="element-7323" data-mp="3qtvrel2jl311">
                      <div className="relative inline-block actions-dropdown" data-id="element-7324" data-mp="12x4z6d9oast">
                        <button onClick={() => setOpenDropdownId(openDropdownId === dispute.id ? null : dispute.id)} aria-label="Dispute actions" aria-expanded={openDropdownId === dispute.id} className="p-2 hover:bg-gray-100 rounded-md transition-colors" data-id="element-7325" data-mp="62jilgx0bt25">
                          <MoreVerticalIcon size={18} className="text-gray-600" aria-hidden="true" data-id="element-7326" data-mp="3o85ze0m15x85" />
                        </button>

                        {openDropdownId === dispute.id && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)} data-id="element-7327" data-mp="1cvpdztyn5qor" />

                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-7328" data-mp="13v9aikhwejjw">
                              {dispute.status === 'challenged' && <button onClick={() => {
                          setOpenDropdownId(null);
                          navigate(`/dispute-detail/${dispute.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-7329" data-mp="tywj5t9tshsb">
                                  <FileTextIcon size={16} className="mr-3 text-gray-500" data-id="element-7330" data-mp="b6vay0b5vf68" />

                                  {t('pages.DisputeDetail.view_challenge_details')}
                                </button>}
                              {dispute.status === 'requires_response' && <button onClick={() => handleAcceptDispute(dispute.id)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-7331" data-mp="3nr9998r966j1">
                                  <CheckIcon size={16} className="mr-3 text-gray-500" data-id="element-7332" data-mp="ajxonwzlqecs" />

                                  {t('pages.DisputeDetail.accept')}
                                </button>}
                              <button onClick={() => {
                          setOpenDropdownId(null);
                          navigate(`/dispute-detail/${dispute.id}`);
                        }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-7333" data-mp="2npujzm8e4hcy">
                                <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-7334" data-mp="24stdfwz6u96w" />

                                {t('pages.ChallengeDispute.view_details')}
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {filteredDisputes.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-7335" data-mp="1il76j2by4jl8">
            <AlertTriangleIcon size={48} className="text-gray-400 mb-4" data-id="element-7336" data-mp="1nk0n53mj4qpm" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-7337" data-mp="1sibth4dm8j33">
              {t('pages.Disputes.no_disputes_found')}
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-7338" data-mp="1x9k3a1iuy6gs">
              {t('pages.Disputes.no_disputes_match_your_current_search')}
            </p>
          </div>}
      </div>

      {/* Bulk Accept Confirmation Modal */}
      {showBulkAcceptModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7339" data-mp="2b8tbadwwtu9o">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7340" data-mp="2b9cwc3huwyrx">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowBulkAcceptModal(false)} data-id="element-7341" data-mp="2g5a6tt6fvaws">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7342" data-mp="luepo2h6unie"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-7343" data-mp="eb5q10yhi36j">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-7344" data-mp="4jhv2q2py40b">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-7345" data-mp="x8or9iepahie">
                <div className="flex items-start gap-4" data-id="element-7346" data-mp="x86xqtjfahe6">
                  <div className="flex-shrink-0" data-id="element-7347" data-mp="s9lwqrwcjtoo">
                    <div className="w-12 h-12 rounded-full bg-[#F1786F] flex items-center justify-center" data-id="element-7348" data-mp="s7so5726aze1">
                      <AlertTriangleIcon size={24} className="text-[#9F140F]" aria-hidden="true" data-id="element-7349" data-mp="nclt0nmyz30q" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-7350" data-mp="331ndq645druz">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-7351" data-mp="33419kh6nj83l">
                      {t('pages.DisputeDetail.accept')} {selectedDisputes.size}{' '}
                      {t('pages.Disputes.dispute')}
                      {selectedDisputes.size !== 1 ? 's' : ''}?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-7352" data-mp="2b26mkpny7y2f">
                      {t('pages.Disputes.accepting')}{' '}
                      {selectedDisputes.size === 1 ? 'this dispute' : 'these disputes'}{' '}
                      {t('pages.Disputes.is_final_once_you_accept_the')}
                      {selectedDisputes.size !== 1 ? 's' : ''}{' '}
                      {t('pages.Disputes.will_receive_a_refund_the_funding')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-7353" data-mp="zlq0welswl74">
                <button onClick={() => setShowBulkAcceptModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-7354" data-mp="umvmdouv9ist">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
                <button onClick={confirmBulkAccept} className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700" data-id="element-7355" data-mp="2wjw2o9ubhn9m">
                  {t('pages.DisputeDetail.accept_dispute')}
                  {selectedDisputes.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}